// Daten-getriebene Landingpages für RegalAbstand.de (PRD §14/§15).
// SeoLandingPage rendert daraus generisch. calculator: "shelf" | null.
// Slugs synchron halten mit scripts/generate-sitemap.js.

export const GROUPS = {
  rechner: "Regal-Rechner",
  schrank: "Schrank & Einbau",
  diy: "DIY & Bohren",
  grundlagen: "Grundlagen",
};

const COMMON_VALUES = [
  "Lichte Innenhöhe des Regals oder Schranks",
  "Anzahl der Fächer oder der Regalböden",
  "Bodenstärke der Einlegeböden",
  "optional eine Rundung der Markiermaße",
];

const COMMON_MISTAKES = [
  "Außenhöhe statt Innenhöhe verwenden.",
  "Fächer und Regalböden verwechseln. Bei 5 Fächern brauchst du 4 Zwischenböden.",
  "Die Bodenstärke vergessen, dadurch werden die Fächer ungleichmäßig.",
];

const COMMON_FAQ = [
  { q: "Zähle ich Fächer oder Böden?", a: "Beides geht. Wähle oben den passenden Modus. Bei 5 Fächern liegen 4 Zwischenböden dazwischen, der Rechner rechnet das automatisch um." },
  { q: "Muss ich die Bodenstärke berücksichtigen?", a: "Ja. Jeder Boden verbraucht Höhe. Der Rechner zieht die Summe der Bodenstärken von der Innenhöhe ab, bevor er die Fachhöhe bestimmt." },
  { q: "Von wo aus messe ich die Markierungen?", a: "Standardmäßig ab der Innen-Unterkante bis zur Unterkante des jeweiligen Bodens. So überträgst du die Maße direkt mit dem Maßband." },
];

export const PAGES = {
  "regalboeden-abstand-rechner": {
    slug: "regalboeden-abstand-rechner",
    group: GROUPS.rechner,
    calculator: "shelf",
    navLabel: "Regalböden-Abstand berechnen",
    h1: "Regalböden-Abstand berechnen",
    metaTitle: "Regalböden-Abstand berechnen: Fachhöhen & Markierliste",
    metaDescription:
      "Berechne gleichmäßige Abstände für Regalböden. Mit Fachhöhe, Bodenstärke, Markiermaßen und einfacher Skizze.",
    intro:
      "Mit dem Regalböden-Abstandsrechner bekommst du aus wenigen Maßen die Fachhöhe und eine genaue Markierliste. Gib Innenhöhe, Anzahl der Böden und Bodenstärke ein.",
    forWhat:
      "Für Regale, Schränke und Nischen, in denen du Einlegeböden gleichmäßig verteilen willst.",
    whichValues: COMMON_VALUES,
    howItWorks:
      "Der Rechner zieht die Summe der Bodenstärken von der Innenhöhe ab und verteilt die verbleibende Höhe gleichmäßig auf alle Fächer. Daraus ergeben sich die Markiermaße für jede Bodenunterkante.",
    example: {
      title: "Beispiel: Innenhöhe 180 cm, 5 Fächer, Bodenstärke 1,8 cm",
      lines: [
        "Regalböden: 4, Bodenstärke gesamt: 7,2 cm.",
        "Verfügbare Fachhöhe gesamt: 172,8 cm, Fachhöhe: 34,56 cm.",
        "Markierungen ab Unterkante: 34,56 / 70,92 / 107,28 / 143,64 cm.",
      ],
    },
    mistakes: COMMON_MISTAKES,
    faq: COMMON_FAQ,
    related: ["regalboeden-gleichmaessig-verteilen", "regal-fachhoehe-berechnen", "einlegeboeden-abstand-rechner", "regal-bohrloecher-berechnen"],
  },

  "regalboeden-gleichmaessig-verteilen": {
    slug: "regalboeden-gleichmaessig-verteilen",
    group: GROUPS.rechner,
    calculator: "shelf",
    navLabel: "Regalböden gleichmäßig verteilen",
    h1: "Regalböden gleichmäßig verteilen",
    metaTitle: "Regalböden gleichmäßig verteilen: Fachhöhe und Markiermaße",
    metaDescription:
      "So verteilst du Regalböden gleichmäßig. Der Rechner zeigt Fachhöhe, Bodenstärke und die Markiermaße für jede Bodenunterkante.",
    intro:
      "Gleichmäßige Fächer entstehen erst, wenn die Bodenstärke mitgerechnet wird. Trag die Werte ein, der Rechner verteilt die Höhe sauber auf alle Fächer.",
    forWhat: "Für Regale, bei denen alle Fächer dieselbe Höhe haben sollen.",
    whichValues: COMMON_VALUES,
    howItWorks:
      "Die verfügbare Höhe nach Abzug der Böden wird durch die Anzahl der Fächer geteilt. So bekommt jedes Fach dieselbe Höhe.",
    example: {
      title: "Beispiel: Innenhöhe 200 cm, 4 Böden, Bodenstärke 2 cm",
      lines: [
        "Fächer: 5, Bodenstärke gesamt: 8 cm.",
        "Verfügbare Fachhöhe gesamt: 192 cm, Fachhöhe: 38,4 cm.",
        "Markierungen ab Unterkante: 38,4 / 78,8 / 119,2 / 159,6 cm.",
      ],
    },
    mistakes: COMMON_MISTAKES,
    faq: COMMON_FAQ,
    related: ["regalboeden-abstand-rechner", "regal-fachhoehe-berechnen", "regal-selber-bauen-abstaende"],
  },

  "einlegeboeden-abstand-rechner": {
    slug: "einlegeboeden-abstand-rechner",
    group: GROUPS.schrank,
    calculator: "shelf",
    navLabel: "Einlegeböden-Abstand",
    h1: "Einlegeböden-Abstand berechnen",
    metaTitle: "Einlegeböden-Abstand berechnen: Fachhöhe im Schrank",
    metaDescription:
      "Berechne den Abstand für Einlegeböden im Schrank. Mit Fachhöhe, Bodenstärke und Markiermaßen ab Innen-Unterkante.",
    intro:
      "Du rüstest Einlegeböden nach und willst sie gleichmäßig setzen? Gib die lichte Innenhöhe, die Anzahl und die Bodenstärke ein.",
    forWhat: "Für Schränke und Korpusse, in denen Einlegeböden nachgerüstet werden.",
    whichValues: COMMON_VALUES,
    howItWorks:
      "Der Rechner behandelt Einlegeböden wie Zwischenböden. Er zieht ihre Stärke ab und verteilt den Rest gleichmäßig auf die Fächer.",
    example: {
      title: "Beispiel: Innenhöhe 180 cm, 3 Einlegeböden, Bodenstärke 1,8 cm",
      lines: [
        "Fächer: 4, verfügbare Fachhöhe: 174,6 cm.",
        "Fachhöhe: 43,65 cm.",
        "Markiere die Unterkanten ab Innen-Unterkante.",
      ],
    },
    mistakes: COMMON_MISTAKES,
    faq: COMMON_FAQ,
    related: ["regalboeden-abstand-rechner", "schrank-einlegeboeden-abstand", "regal-fachhoehe-berechnen"],
  },

  "regal-fachhoehe-berechnen": {
    slug: "regal-fachhoehe-berechnen",
    group: GROUPS.rechner,
    calculator: "shelf",
    navLabel: "Regal-Fachhöhe berechnen",
    h1: "Regal-Fachhöhe berechnen",
    metaTitle: "Regal-Fachhöhe berechnen: gleichmäßige Fächer planen",
    metaDescription:
      "Berechne die Fachhöhe deines Regals. Innenhöhe, Anzahl und Bodenstärke eingeben, der Rechner zeigt die gleichmäßige Fachhöhe.",
    intro:
      "Wie hoch werden die Fächer? Der Rechner zeigt dir die gleichmäßige Fachhöhe und die passenden Markiermaße.",
    forWhat: "Wenn du vorab wissen willst, wie hoch die einzelnen Fächer werden.",
    whichValues: COMMON_VALUES,
    howItWorks:
      "Fachhöhe = (Innenhöhe minus Summe der Bodenstärken) geteilt durch die Anzahl der Fächer.",
    example: {
      title: "Beispiel: Innenhöhe 180 cm, 5 Fächer, Bodenstärke 1,8 cm",
      lines: ["Verfügbare Höhe: 172,8 cm.", "Fachhöhe: 34,56 cm."],
    },
    mistakes: COMMON_MISTAKES,
    faq: COMMON_FAQ,
    related: ["regalboeden-abstand-rechner", "regalboeden-gleichmaessig-verteilen", "schrank-einlegeboeden-abstand"],
  },

  "regal-bohrloecher-berechnen": {
    slug: "regal-bohrloecher-berechnen",
    group: GROUPS.diy,
    calculator: "shelf",
    navLabel: "Bohrlöcher berechnen",
    h1: "Bohrlöcher für Regalböden berechnen",
    metaTitle: "Bohrlöcher für Regalböden berechnen: Markiermaße",
    metaDescription:
      "Berechne die Markiermaße für die Bohrlöcher deiner Regalböden. Gleichmäßige Fachhöhen und Positionen ab Innen-Unterkante.",
    intro:
      "Bevor du bohrst, brauchst du die genauen Positionen. Der Rechner liefert die Markiermaße für jede Bodenunterkante.",
    forWhat: "Für das Setzen von Bodenträgern oder Bohrlöchern in gleichmäßigen Abständen.",
    whichValues: COMMON_VALUES,
    howItWorks:
      "Die Markierung sitzt standardmäßig an der Bodenunterkante. Den Abstand vom Boden zum Bohrloch kannst du anschließend konstant nach oben oder unten übertragen.",
    example: {
      title: "Beispiel: Innenhöhe 180 cm, 5 Fächer, Bodenstärke 1,8 cm",
      lines: ["Markierungen ab Unterkante: 34,56 / 70,92 / 107,28 / 143,64 cm.", "Übertrag den Bohrlochabstand konstant auf beide Seiten."],
    },
    mistakes: COMMON_MISTAKES,
    faq: COMMON_FAQ,
    related: ["regalboeden-abstand-rechner", "bohrloecher-regalboeden-markieren", "32mm-lochreihe-rechner"],
  },

  "schrank-einlegeboeden-abstand": {
    slug: "schrank-einlegeboeden-abstand",
    group: GROUPS.schrank,
    calculator: "shelf",
    navLabel: "Schrank-Einlegeböden",
    h1: "Schrank-Einlegeböden Abstand berechnen",
    metaTitle: "Schrank-Einlegeböden Abstand berechnen: Fachhöhe",
    metaDescription:
      "Berechne den Abstand der Einlegeböden im Schrank. Innenhöhe, Anzahl und Bodenstärke eingeben, Markiermaße erhalten.",
    intro:
      "Im Schrank zählt die lichte Innenhöhe. Trag sie ein, der Rechner verteilt die Einlegeböden gleichmäßig.",
    forWhat: "Für Kleiderschränke, Vorratsschränke und Korpusse mit Einlegeböden.",
    whichValues: COMMON_VALUES,
    howItWorks:
      "Miss die lichte Innenhöhe zwischen Unterseite des oberen und Oberseite des unteren Bodens. Der Rechner verteilt die Einlegeböden dazwischen.",
    example: {
      title: "Beispiel: Innenhöhe 160 cm, 3 Einlegeböden, Bodenstärke 1,8 cm",
      lines: ["Fächer: 4, verfügbare Höhe: 154,6 cm.", "Fachhöhe: 38,65 cm."],
    },
    mistakes: COMMON_MISTAKES,
    faq: COMMON_FAQ,
    related: ["einlegeboeden-abstand-rechner", "regal-fachhoehe-berechnen", "regalboeden-abstand-rechner"],
  },

  "regal-selber-bauen-abstaende": {
    slug: "regal-selber-bauen-abstaende",
    group: GROUPS.diy,
    calculator: "shelf",
    navLabel: "Regal selber bauen",
    h1: "Regal selber bauen: Abstände berechnen",
    metaTitle: "Regal selber bauen: Abstände und Fachhöhen berechnen",
    metaDescription:
      "Plane die Abstände für dein selbstgebautes Regal. Fachhöhen, Bodenstärke und Markiermaße in einem Rechner.",
    intro:
      "Beim Selbstbau entscheidet die saubere Aufteilung über das Ergebnis. Der Rechner gibt dir die Fachhöhen und Markiermaße.",
    forWhat: "Für DIY-Regale aus Holz oder Platten, die du selbst zuschneidest und montierst.",
    whichValues: COMMON_VALUES,
    howItWorks:
      "Gib die geplante Innenhöhe und die Anzahl der Fächer ein. Der Rechner berücksichtigt die Bodenstärke und liefert die Positionen.",
    example: {
      title: "Beispiel: Innenhöhe 200 cm, 5 Fächer, Bodenstärke 2 cm",
      lines: ["Böden: 4, verfügbare Höhe: 192 cm.", "Fachhöhe: 38,4 cm."],
    },
    mistakes: COMMON_MISTAKES,
    faq: COMMON_FAQ,
    related: ["regalboeden-gleichmaessig-verteilen", "regal-bohrloecher-berechnen", "regalboeden-abstand-rechner"],
  },

  "bohrloecher-regalboeden-markieren": {
    slug: "bohrloecher-regalboeden-markieren",
    group: GROUPS.diy,
    calculator: "shelf",
    navLabel: "Regalböden markieren",
    h1: "Bohrlöcher für Regalböden markieren",
    metaTitle: "Bohrlöcher für Regalböden markieren: Markierliste",
    metaDescription:
      "Erstelle eine Markierliste für die Bohrlöcher deiner Regalböden. Gleichmäßige Abstände, kopierbar und druckbar.",
    intro:
      "Eine saubere Markierliste spart Fehler. Der Rechner erstellt sie für dich, zum Kopieren oder Drucken.",
    forWhat: "Für die Übertragung der Bohrpunkte auf die Seitenwände.",
    whichValues: COMMON_VALUES,
    howItWorks:
      "Markiere konsequent ab der Innen-Unterkante. So sind beide Seitenwände identisch und die Böden liegen waagerecht.",
    example: {
      title: "Beispiel: Innenhöhe 180 cm, 4 Böden, Bodenstärke 1,8 cm",
      lines: ["Markierungen ab Unterkante übertragen.", "Auf beiden Seiten dieselbe Liste verwenden."],
    },
    mistakes: COMMON_MISTAKES,
    faq: COMMON_FAQ,
    related: ["regal-bohrloecher-berechnen", "regalboeden-abstand-rechner", "32mm-lochreihe-rechner"],
  },

  "32mm-lochreihe-rechner": {
    slug: "32mm-lochreihe-rechner",
    group: GROUPS.diy,
    calculator: "shelf",
    comingSoon: true,
    navLabel: "32-mm-Lochreihe",
    h1: "32-mm-Lochreihe Rechner",
    metaTitle: "32-mm-Lochreihe Rechner: Fachhöhen im System",
    metaDescription:
      "Plane Fachhöhen nach dem 32-mm-System. Dieser Modus kommt in Kürze, solange helfen die anderen Regal-Rechner.",
    intro:
      "Das 32-mm-System arbeitet mit festen Lochabständen. Diesen Modus bauen wir gerade, er rundet Fachhöhen auf das nächste Loch.",
    forWhat: "Für Möbel mit Lochreihen nach dem 32-mm-Standard.",
    whichValues: COMMON_VALUES,
    howItWorks:
      "Der Rechner sucht die nächstgelegene Lochposition zur idealen Fachhöhe und zeigt die Abweichung. Bis dahin nutze den normalen Fächer-Modus.",
    faq: [
      { q: "Was ist das 32-mm-System?", a: "Ein verbreiteter Standard im Möbelbau mit Lochreihen im Abstand von 32 Millimetern. Bodenträger und Beschläge sitzen in diesen Löchern." },
      ...COMMON_FAQ,
    ],
    related: ["regal-bohrloecher-berechnen", "bohrloecher-regalboeden-markieren", "regalboeden-abstand-rechner"],
  },
};

export const PAGE_LIST = Object.values(PAGES);

export function getPage(slug) {
  return PAGES[slug] || null;
}
